import { useSyncExternalStore } from 'react';

export type Breakpoint = 'phone' | 'tablet' | 'desktop';

/**
 * Breakpoints per .agents/frontend-responsive-plan.md (recalibrated after
 * on-device testing; overridden in globals.css @theme): phone = base
 * (≤ 860px), tablet = `sm:`…`max-lg:` (861–1280px), desktop = `lg:`
 * (≥ 1281px).
 *
 * Queries are rem-based to match the media queries Tailwind emits for
 * `sm:`/`lg:` (53.8125rem/80.0625rem — 861px/1281px at the 16px browser
 * default font size). px values here would diverge from the CSS rules
 * whenever the user changes their browser's default font size.
 *
 * Use this hook ONLY for branches that change which component or route
 * mounts (inspector-as-panel vs inspector-as-route, swimlane vs agenda).
 * Pure styling differences belong in CSS via `sm:` / `lg:` prefixes.
 */
const TABLET_QUERY = '(min-width: 53.8125rem)';
const DESKTOP_QUERY = '(min-width: 80.0625rem)';

function getSnapshot(): Breakpoint {
  if (window.matchMedia(DESKTOP_QUERY).matches) return 'desktop';
  if (window.matchMedia(TABLET_QUERY).matches) return 'tablet';
  return 'phone';
}

// Before the viewport can be measured (SSR / non-DOM environments), report
// `desktop` so the existing desktop install base never flashes a phone layout.
function getServerSnapshot(): Breakpoint {
  return 'desktop';
}

function subscribe(onChange: () => void): () => void {
  const lists = [DESKTOP_QUERY, TABLET_QUERY].map((query) => window.matchMedia(query));
  for (const list of lists) list.addEventListener('change', onChange);
  return () => {
    for (const list of lists) list.removeEventListener('change', onChange);
  };
}

export function useBreakpoint(): {
  breakpoint: Breakpoint;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
} {
  const breakpoint = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    breakpoint,
    isPhone: breakpoint === 'phone',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop',
  };
}
